import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';
import type { UserData } from './user/types';

export type { UserData } from './user/types';

export async function getUserData(userId: string): Promise<UserData | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    return userDoc.data() as UserData;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch user data');
  }
}

export async function updateUserData(userId: string, data: Partial<UserData>): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const timestamp = serverTimestamp();

    // Get current data first
    const currentDoc = await getDoc(userRef);

    if (!currentDoc.exists()) {
      // If document doesn't exist, create it
      await setDoc(userRef, {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
    } else {
      // If document exists, update it
      await updateDoc(userRef, {
        ...data,
        updatedAt: timestamp,
      });
    }
  } catch (error) {
    console.error('Error updating user data:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to update user data');
  }
}

/**
 * Convert an arbitrary string to a URL-safe path segment.
 */
export function toPathSegment(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * Check whether a given path is available (no other user has it).
 */
export async function isPathAvailable(path: string, excludeUserId?: string): Promise<boolean> {
  // Reads the public `paths/{slug} -> { userId }` mapping instead of running a
  // collection query over /users. Same answer, but it does not require the
  // caller to be able to LIST /users — which is what let any signed-in user
  // enumerate every user document and harvest their email addresses.
  // resolveUserIdFromIdentifier() below already prefers this same mapping, and
  // it is written on every path change in DashboardLayout, so it is the
  // authoritative record of which slugs are taken.
  const { doc, getDoc } = await import('firebase/firestore');
  const snap = await getDoc(doc(db, 'paths', path));
  if (!snap.exists()) return true;
  const owner = (snap.data() as { userId?: string } | undefined)?.userId;
  // A mapping with no userId is malformed; treat the slug as taken rather than
  // handing it out and creating a collision.
  if (excludeUserId && owner === excludeUserId) return true;
  return false;
}

/**
 * Generate a unique path based on first and last name.
 * Examples: john-doe, john-doe-1, john-doe-2
 */
export async function generateUniquePath(firstName?: string, lastName?: string, excludeUserId?: string): Promise<string> {
  const baseRaw = `${firstName || ''} ${lastName || ''}`.trim() || 'user';
  const base = toPathSegment(baseRaw) || 'user';
  if (await isPathAvailable(base, excludeUserId)) return base;
  let counter = 1;
  // Cap attempts to avoid infinite loops
  while (counter < 1000) {
    const candidate = `${base}-${counter}`;
    if (await isPathAvailable(candidate, excludeUserId)) return candidate;
    counter += 1;
  }
  // Fallback with timestamp
  return `${base}-${Date.now()}`;
}

/**
 * Resolve a userId from a given identifier which may be a UID or a path.
 */
export async function resolveUserIdFromIdentifier(identifier: string): Promise<string | null> {
  // Fast path: only if identifier looks like a Firebase UID (typically 28 chars, URL-safe)
  const looksLikeUid = /^[A-Za-z0-9_-]{20,}$/.test(identifier);
  if (looksLikeUid) {
    try {
      // Prefer checking for a fund page document directly; this does not
      // require the presence of a public users/{uid} document.
      const pageSnap = await getDoc(doc(db, 'missionpages', identifier));
      if (pageSnap.exists()) return identifier;
    } catch { /* ignore and try user doc next */ }
    try {
      const direct = await getUserData(identifier);
      if (direct) return identifier;
    } catch { /* ignore and fall back to path lookup */ }
  }

  // Otherwise, resolve via public mapping collection `paths/{slug}` -> { userId }
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const mapping = await getDoc(doc(db, 'paths', identifier));
    if (mapping.exists()) {
      const data = mapping.data();
      return data.userId || null;
    }
  } catch (_) { /* ignore */ }

  // No match via UID fast-path or the public `paths/{slug}` mapping.
  // There used to be two further fallbacks here -- scanning `users` by its
  // `path` field, then scanning `missions` by `slug` -- but firestore.rules
  // denies `list` on both collections unconditionally (`allow list: if
  // false`) for every caller, signed-in or not. The `users` query could
  // never succeed and only logged a "Missing or insufficient permissions"
  // console error on every unresolved identifier (TSF-213, ported from
  // sportfund). The `missions` query was already unreachable dead code
  // before this fix (every branch of the `users` try above returned), which
  // is the only reason firestore.rules could safely close its own `list` to
  // `false` (see the comment at firestore.rules `match /missions/{missionId}`)
  // -- so it must stay unreachable, not be "fixed" into a live call that
  // would just hit the same permission-denied wall one collection later.
  return null;
}
